import { Input } from 'antd';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import type { ChangeEvent, ReactElement, ReactNode } from 'react';

import CrossIcon from 'src/shared/assets/icons/cross.svg';

import { InputError } from '../input-error';
import { InputLabel } from '../input-label';

import styles from './form-input.module.scss';

type Props = {
  className?: string;
  allowClear?: boolean;
  value?: number | string | null;
  label?: string | ReactElement;
  isError?: boolean;
  errorText?: string | [string, { [locKey: string]: string | number }];
  prefix?: ReactNode;
  suffixComponent?: ReactNode;
  unit?: string;
  placeholder?: string;
  isDisabled?: boolean;
  onChange?: (value: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

export const FormInput = observer(function FormInput({
  className,
  value,
  allowClear = true,
  label,
  isError,
  errorText,
  prefix,
  suffixComponent,
  unit,
  placeholder,
  isDisabled,
  onChange,
  onFocus,
  onBlur,
}: Props) {
  const labelRender = (): JSX.Element | undefined => {
    if (typeof label === 'string') {
      return <InputLabel text={label} />;
    }

    return label;
  };

  return (
    <div className={clsx(styles.inputWrapper, className)}>
      {labelRender()}

      <Input
        value={value === null ? '' : value}
        placeholder={placeholder}
        className={clsx(styles.input, isDisabled && styles.input__disabled)}
        prefix={prefix ? <p className={styles.text}>{prefix}</p> : undefined}
        suffix={
          <>
            {suffixComponent}
            <p className={styles.text}>{unit}</p>
          </>
        }
        allowClear={
          allowClear && {
            clearIcon: <CrossIcon className={styles.icon} />,
          }
        }
        status={isError ? 'error' : undefined}
        disabled={isDisabled}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      {!!errorText && <InputError errorTextRaw={errorText} />}
    </div>
  );
});
